import React from "react";
import { FormikErrors, useFormik } from "formik";
import { useTranslation } from "react-i18next";

import { prependServiceBaseUrl } from "../../../../commons/libs/client-utils";
import { ensureError } from "../../../../commons/libs/error";
import envelope from "../../../../commons/libs/externals/envelope";
import { KeyboardType } from "../../../../commons/types/keyboard";
import { ModalProps, PrintMailModalContentProps } from "../../../../commons/types/modal";
import { PrintType } from "../../../../commons/types/print-mail";
import * as icons from "../../../../resources/icons";
import { ROUTES } from "../../../commons/routes";
import Button from "../../components/Button/Button";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import IconButton from "../../components/IconButton/IconButton";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import Modal from "../../components/Modal/Modal";
import Paragraph from "../../components/Paragraph/Paragraph";
import PdfPage from "../../components/PdfPage/PdfPage";
import PinField from "../../components/PinField/PinField";
import PrintModalLayout from "../../components/PrintModalLayout/PrintModalLayout";
import PrintPreview from "../../components/PrintPreview/PrintPreview";
import RadioField from "../../components/RadioField/RadioField";
import RadioGroupWithOther from "../../components/RadioGroupWithOther/RadioGroupWithOther";
import config from "../../config";
import { matchesSha256Hex } from "../../libs/externals/hash";
import { useAppHistory } from "../../libs/hooks/use-app-routing";
import print, { createPreviewUrl } from "../../libs/print";
import { useGetPdfMetaDataFromUrl } from "../../libs/queries/use-pdf";
import { useSettings } from "../../libs/queries/use-settings";
import { getPrintQueue, PrinterStatusResponse } from "../../libs/services/print-service/print";
import { useCommonAppDispatcher } from "../../store/hooks";
import FormPartial from "../partials/Form";
import LabeledField from "../partials/LabeledField";
import { useTrackingContext } from "../utils/tracking-context";

interface FormValues {
  code: string;
  numCopies: string;
}

interface Props extends ModalProps<PrintMailModalContentProps> {
  isOffline: boolean;
  isUsbPrinterConnected: boolean;
  printerStatus?: PrinterStatusResponse;
  nodeRef?: React.Ref<HTMLElement>;
  requestKeyboardClose?: () => void;
}

const NumberOfCopies = (props: Omit<React.ComponentProps<typeof RadioGroupWithOther>, "children">) => (
  <RadioGroupWithOther {...props} block>
    <RadioField label="1" value="1" />
    <RadioField label="2" value="2" />
  </RadioGroupWithOther>
);

const PrintModalPartial = ({
  data: printData,
  close,
  isOffline,
  isUsbPrinterConnected,
  printerStatus,
  nodeRef,
  requestKeyboardClose
}: Props) => {
  const isMobile = envelope.isMobileBuild;

  const [dispatch, actions] = useCommonAppDispatcher();
  const history = useAppHistory();
  const { t } = useTranslation(["commons"]);

  const settings = useSettings();
  const { mixpanel } = useTrackingContext();

  const formik = useFormik<FormValues>({
    initialValues: {
      code: "",
      numCopies: "1"
    },
    validateOnMount: true,
    validate: values => {
      const errors: FormikErrors<FormValues> = {};

      if (!values.numCopies || values.numCopies.match(/[^0-9]/)) {
        errors.numCopies = t("commons:printModalPartialApp.errors.invalidPrintQuantity");
      } else if (parseInt(values.numCopies, 10) <= 0) {
        errors.numCopies = t("commons:printModalPartialApp.errors.minOneCopyRequired");
      } else if (parseInt(values.numCopies, 10) > config.shared.printMaxCopies) {
        errors.numCopies = t("commons:printModalPartialApp.errors.maxCopiesAllowed", {
          count: config.shared.printMaxCopies
        });
      }

      if (
        settings.initialized.printing.pinRequired &&
        !matchesSha256Hex(values.code, settings.initialized.personalCode ?? "")
      ) {
        errors.code = t("commons:printModalPartialApp.errors.invalidPersonalCode");
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting, setTouched }) => {
      if (printData.type === PrintType.ProductDataSheet) {
        printData.items.forEach(item =>
          mixpanel?.trackProductPrinted(
            { type: printData.type, numCopies: values.numCopies, initiator: printData.initiator },
            item.product,
            item.variantId
          )
        );
      } else {
        mixpanel?.trackPrinted({ type: printData.type, numCopies: values.numCopies });
      }

      requestKeyboardClose?.();
      close();

      try {
        await print(printData, parseInt(values.numCopies, 10), settings.initialized.printing.mailToPrint);
        const printQueue = await getPrintQueue();
        const toastTitle = t("commons:printModalPartialApp.docsInPrint", { count: printQueue.length });

        const printTypeTitle = {
          [PrintType.ProductDataSheet as string]: t("commons:printModalPartialApp.dataSheetIsPrintedTitle"),
          [PrintType.Default as string]: t("commons:printModalPartialApp.isPrintedTitle")
        };

        dispatch(
          actions.toasts.showToast({
            title: `${printTypeTitle[printData.type]}: ${toastTitle}`,
            timeout: 10 * 1000,
            icon: icons.IconSmallPrinter
          })
        );
      } catch (error) {
        const model =
          printerStatus && printerStatus.length > 0 && printerStatus[0]
            ? printerStatus[0].model
            : t("commons:printModalPartialApp.cloudPrinting");

        dispatch(
          actions.toasts.showToast({
            title: t("commons:printModalPartialApp.errorWhilePrintNotification.errorWhilePrintTitle"),
            timeout: 5 * 1000,
            icon: icons.IconSmallPrinter,
            kind: "negative",
            primaryAction: {
              label: t("commons:printModalPartialApp.errorWhilePrintNotification.moreInformationLabel"),
              callback: () =>
                dispatch(
                  actions.modal.showModal("error", {
                    type: "print",
                    errorMessage: ensureError(error).message,
                    headline: t("commons:printModalPartialApp.errorWhilePrintNotification.moreInformationHeadline"),
                    paragraph: t(
                      "commons:printModalPartialApp.errorWhilePrintNotification.errorWhilePrintExplanation",
                      {
                        product: model
                      }
                    )
                  })
                )
            }
          })
        );

        mixpanel?.trackPrintFailed();
      }

      setSubmitting(false);
      setTouched({});
    }
  });

  const previewUrl = React.useMemo(() => createPreviewUrl(printData), [printData]);
  const previewPdf = useGetPdfMetaDataFromUrl({ url: previewUrl });

  React.useEffect(() => {
    if (formik.values.code.length === config.shared.personalCodeLength) {
      formik.setFieldTouched("code");
      requestKeyboardClose?.();
    }
    // formik and keyboard close function must not be deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.code]);

  const pdfPreviewSource =
    !previewPdf.isLoading && previewPdf?.data?.pages && previewPdf?.data?.pages?.length > 0
      ? prependServiceBaseUrl(previewPdf.data.pages[0])
      : "";

  const pdfUrl = !previewPdf.isLoading && previewPdf?.data?.url ? prependServiceBaseUrl(previewPdf?.data?.url) : "";

  const renderPrintFormContent = React.useCallback(
    () => (
      <>
        <Headline kind="m" classNames={["u-space-l"]}>
          {t("commons:printModalPartialApp.printPreviewHeadline")}
        </Headline>
        <FormPartial formik={formik}>
          <LabeledField
            formik={formik}
            label={t("commons:printModalPartialApp.copiesLabel")}
            classNames={["u-space-l"]}
          >
            <NumberOfCopies
              name="numCopies"
              placeholder={t("commons:printModalPartialApp.quantityLabel")}
              keyboardType={KeyboardType.Numpad}
            />
          </LabeledField>
          {settings.initialized.printing.pinRequired && (
            <LabeledField
              formik={formik}
              hideErrorText
              label={t("commons:printModalPartialApp.enterPersonalCodeToPrintLabel")}
              classNames={["u-space-l"]}
            >
              <PinField name="code" length={config.shared.personalCodeLength} />
            </LabeledField>
          )}
          <Button
            type="submit"
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              !formik.values ||
              (formik.values?.code?.length !== config.shared.personalCodeLength &&
                settings.initialized.printing.pinRequired)
            }
            variant="accent"
            kind="solid"
            icon={formik.isSubmitting ? <LoadingIndicator size="s" /> : <Icon source={icons.IconSmallPrinter} />}
            iconRight
          >
            {t("commons:printModalPartialApp.printNowButton")}
          </Button>
        </FormPartial>
      </>
    ),
    [formik, settings.initialized.printing.pinRequired, t]
  );

  const renderKioskContent = React.useCallback(() => {
    if (!settings.hasMailToPrintSettings && !isUsbPrinterConnected) {
      return (
        <>
          <Headline kind="m" classNames={["u-space-l"]}>
            {t("commons:printModalPartialApp.noPrinterEnabled.noPrinterConnectedHeadline")}
          </Headline>
          <span>
            <Button
              variant="accent"
              kind="solid"
              icon={<Icon source={icons.IconSmallArrowRight} />}
              iconRight
              onClick={() => {
                close({ preventReopenPreviousModal: false });
                history.push(ROUTES.SETTINGS.PRINTER_INDEX);
              }}
            >
              {t("commons:printModalPartialApp.noPrinterEnabled.setUpPrinterButton")}
            </Button>
          </span>
        </>
      );
    } else if (settings.hasMailToPrintSettings && isOffline) {
      return (
        <>
          <Headline kind="m" classNames={["u-space-l"]}>
            {t("commons:printModalPartialApp.needsOnlineConnection.deviceOfflineHeadline")}
          </Headline>
          <span>
            <Button
              classNames={["u-space-l"]}
              variant="accent"
              kind="solid"
              icon={<Icon source={icons.IconSmallArrowRight} />}
              iconRight
              onClick={() => {
                close({ preventReopenPreviousModal: false });
                history.push(ROUTES.SETTINGS.NETWORK_INDEX);
              }}
            >
              {t("commons:printModalPartialApp.needsOnlineConnection.connectNowButton")}
            </Button>
            <Paragraph kind="support">
              {t("commons:printModalPartialApp.needsOnlineConnection.internetIsRequiredForCloudPrinting")}
            </Paragraph>
          </span>
        </>
      );
    } else {
      return renderPrintFormContent();
    }
  }, [isOffline, isUsbPrinterConnected, settings.hasMailToPrintSettings, t, close, renderPrintFormContent, history]);

  const renderMobileContent = React.useCallback(
    () => (
      <>
        {settings.hasMailToPrintSettings && renderPrintFormContent()}
        <Button
          classNames={["u-space-top-xs"]}
          icon={<Icon source={icons.IconSmallArrowRight} />}
          iconRight
          onClick={() => window.open(pdfUrl)}
          disabled={previewPdf.isLoading}
        >
          {t("commons:printModalPartialApp.openInNewTabButton")}
        </Button>
      </>
    ),
    [pdfUrl, previewPdf.isLoading, t, settings.hasMailToPrintSettings, renderPrintFormContent]
  );

  return (
    <Modal
      nodeRef={nodeRef}
      width="xl"
      closeButton={<IconButton onClick={() => close()} icon={<Icon source={icons.IconSmallCross} />} />}
    >
      <PrintModalLayout
        printPreview={
          <PrintPreview loaded={!previewPdf.isLoading}>
            <PdfPage source={pdfPreviewSource} />
          </PrintPreview>
        }
      >
        {isMobile ? renderMobileContent() : renderKioskContent()}
      </PrintModalLayout>
    </Modal>
  );
};

export default PrintModalPartial;
